import * as React from "react";
import {
  Checkbox,
  CircularProgress,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  SelectProps,
  Typography,
} from "@mui/material";
import _uniqueId from "lodash/uniqueId";
import { UseQueryResult } from "react-query";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { PaginatedResult } from "types/common-types";
import { Selectable } from "components/AutocompleteWithQuery";

const ITEM_HEIGHT = 48;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 6,
    },
  },
  getContentAnchorEl: null,
};

type SelectMultipleEntityProps<T extends Selectable> = {
  values: string[];
  label?: string;
  labelField: keyof T;
  customKeyField?: keyof T;
  fetchedData: UseQueryResult<PaginatedResult<T>>;
  onChange: (selection: string[]) => void;
} & Omit<SelectProps, "label" | "onChange" | "value">;

const SelectMultipleEntity = <T extends Selectable>({
  values,
  label,
  labelField,
  customKeyField,
  fetchedData,
  onChange,
  ...rest
}: SelectMultipleEntityProps<T>) => {
  const { formatMessage: t } = useIntl();
  const { isLoading, isError, data, error } = fetchedData;
  const [selected, setSelected] = useState<string[]>([]);

  const keyField = customKeyField ?? "id";

  useEffect(() => {
    if (!isLoading && data?.data) {
      if (
        values &&
        !data?.data?.some((d) => values.some((v) => v === d[keyField]))
      ) {
        onChange([]);
      }
    }
  }, [isLoading, data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isError) {
    console.log(error);
    return (
      <Typography color="error">{t({ id: "selectFetchFailed" })}</Typography>
    );
  }

  const handleChange = (
    event: SelectChangeEvent<unknown>
  ) => {
    const selection = event.target.value as string[];
    onChange(selection);
    setSelected(selection);
  };

  const options = data?.data ?? [];

  const renderValue = (selectedIds: unknown) => {
    const ids = selectedIds as string[];
    const selection = options.filter((o) =>
      ids.some((id) => id === o[keyField])
    );
    return selection.map((s) => s[labelField]).join(", ");
  };

  const id = _uniqueId();

  return (
    <div>
      <FormControl fullWidth variant="outlined">
        <InputLabel id={id}>{label}</InputLabel>
        <Select
          {...rest}
          MenuProps={MenuProps}
          labelId={id}
          multiple={true}
          value={values}
          onChange={handleChange}
          renderValue={renderValue}
          input={
            <OutlinedInput
              label={label}
              startAdornment={isLoading && <CircularProgress size={16} />}
            />
          }
        >
          {options.map((option) => {
            const id = option[keyField] as string;
            return (
              <MenuItem key={id} value={id}>
                <Checkbox checked={selected.indexOf(id) > -1} />
                <ListItemText primary={option[labelField] as string} />
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </div>
  );
};

export default SelectMultipleEntity;
